<template>
<div>
	<div v-if="!hideHeader" class="d-flex justify-content-between mb-2">
		<h3 style="margin-bottom: 0">
			<i v-if="showCollapseButton" v-on:click="$emit('collapseSummary')" class="fa fa-chevron-right icon-button"></i>
			Bid Summary
		</h3>
		<a v-if="!hideNewTabLink" :href="'/project-bid-summary/' + $route.params.tag" target="_blank">Open Bid Summary in Separate Tab</a>
	</div>

	<table class="table table-sm">
		<thead v-if="showDetailedValues">
			<tr class="bg-dark text-white">
				<th>Item #</th>
				<th v-if="!hideSpecNumber">Spec #</th>
				<th v-if="!hideDescription">Description</th>
				<th class="text-right">Qty</th>
				<th v-if="showDetailedValues" class="text-right">Cost</th>
				<th v-if="!hidePriceAdjustment" class="text-right">Price Adjustment</th>
				<th v-if="!hideMarkup" class="text-right">Markup</th>
				<th class="text-right">Unit Selling</th>
				<th class="text-right">Total Selling</th>
				<th v-if="showDetailedValues" class="text-right">Gross Margin</th>
			</tr>
		</thead>
		<tbody v-for="section in bidComponentsNested">
			<tr class="bg-light" :class="{'active-section' : selectedTopComponentId == section.id}">
				<th colspan="2">{{ section.name }}</th>
				<th v-if="!hideSpecNumber"></th>
				<th v-if="!hideDescription" class="text-right"></th>
				<th v-if="showDetailedValues" class="text-right">{{ section.totalComponentCostRaw.toCurrency() }}</th>
				<th v-if="!hidePriceAdjustment" class="text-right">{{ section.totalComponentAdjustedValueRaw.toCurrency() }}</th>
				<th v-if="!hideMarkup" class="text-right">{{ section.totalComponentMarkupValueRaw.toCurrency() }}</th>
				<th colspan="1"></th>
				<th class="text-right">{{ section.totalPriceRaw.toCurrency() }}</th>
				<th v-if="showDetailedValues" class="text-right">{{ section.componentGrossMarginRaw.toFixed(1) }}%</th>
			</tr>
			<tr v-if="!showDetailedValues">
				<th>Item #</th>
				<th v-if="!hideSpecNumber">Spec #</th>
				<th v-if="!hideDescription">Description</th>
				<th class="text-right">Qty</th>
				<th v-if="showDetailedValues" class="text-right">Cost</th>
				<th v-if="!hidePriceAdjustment" class="text-right">Price Adjustment</th>
				<th v-if="!hideMarkup" class="text-right">Markup</th>
				<th class="text-right">Unit Selling</th>
				<th class="text-right">Total Selling</th>
				<th v-if="showDetailedValues" class="text-right">Gross Margin</th>
			</tr>
			<template v-for="comp in section.JobBidComponent" >
				<tr :class="{'active-section' : selectedComponentId == comp.id}" class="top-level-component">
					<td :title="comp.is_reverse_calculated == 1 && comp.unitPriceRaw.countDecimals() > 2 ? 'This component\'s unit price is not a round number, so the Total Price is subject to rounding errors' : false">
						<i v-if="comp.is_reverse_calculated == 1 && comp.unitPriceRaw.countDecimals() > 2" class="fa fa-exclamation text-warning"></i>
						{{ comp.item_number }}
					</td>
					<td v-if="!hideSpecNumber">{{ comp.spec_number }}</td>
					<td v-if="!hideDescription">{{ comp.name }}<span v-if="comp.description">: {{ comp.description }}</span></td>
					<td class="text-right">{{ comp.qty }}</td>
					<td v-if="showDetailedValues" class="text-right">{{ comp.totalComponentCostRaw.toCurrency() }}</td>
					<td v-if="!hidePriceAdjustment" class="text-right">{{ comp.totalComponentAdjustedValueRaw.toCurrency() }}</td>
					<td v-if="!hideMarkup" class="text-right">{{ comp.totalComponentMarkupValueRaw.toCurrency() }}</td>
					<td class="text-right" :title="comp.unitPriceRaw">{{ comp.unitPriceRaw.toCurrency() }}</td>
					<td class="text-right">{{ comp.totalPriceRaw.toCurrency() }}</td>
					<td v-if="showDetailedValues" class="text-right">{{ comp.componentGrossMarginRaw.toCurrency() }}%</td>
				</tr>
				<template v-if="'JobBidComponent' in comp && comp.JobBidComponent">
					<tr v-for="subComp in comp.JobBidComponent"
						:class="{'active-section' : selectedComponentId == subComp.id}">

						<td>
							<i v-if="subComp.is_reverse_calculated == 1 && subComp.unitPriceRaw.countDecimals() > 2" class="fa fa-exclamation text-warning"></i>
							<span class="text-right">{{ subComp.item_number }}</span>
						</td>
						<td>{{ subComp.spec_number }}</td>
						<td  v-if="!hideDescription">
							{{ subComp.name }}
							<span v-if="subComp.description">: {{ subComp.description }}</span>
						</td>
						<td class="text-right">{{ subComp.qty }}</td>
						<td v-if="showDetailedValues" class="text-right">{{ subComp.totalComponentCostRaw.toCurrency() }}</td>
						<td v-if="!hidePriceAdjustment" class="text-right">{{ subComp.totalComponentAdjustedValueRaw.toCurrency() }}</td>
						<td v-if="!hideMarkup" class="text-right">{{ subComp.totalComponentMarkupValueRaw.toCurrency() }}</td>
						<td class="text-right" :title="subComp.unitPriceRaw">{{ subComp.unitPriceRaw.toCurrency() }}</td>
						<td class="text-right">{{ subComp.totalPriceRaw.toCurrency() }}</td>
						<td v-if="showDetailedValues" class="text-right">{{ subComp.componentGrossMarginRaw.toCurrency() }}%</td>
					</tr>
				</template>
				
			</template>
		</tbody>
		<tfoot>
			<tr style="font-size: 14pt;border-top: 1px solid gray;">
				<th :colspan="grandTotalLabelColSpan" class="text-right">Total Bid Value:</th>
				<th class="text-right">{{ totalBidValue.toCurrency() }}</th>
			</tr>
			<tr style="font-size: 14pt;">
				<th :colspan="grandTotalLabelColSpan" class="text-right">Total Gross Margin:</th>
				<th class="text-right">{{ totalGrossMargin.toFixed(1) }}%</th>
			</tr>
		</tfoot>
	</table>
</div>
</template>
<script type="text/javascript">

import ProjectBidMixins from "@/mixins/ProjectBidMixins.js"
	
export default {
	name: "ProjectBidSummary",
	components: {

	},
	mixins: [ProjectBidMixins],
	props: [
		"bidProp",
		"showCollapseButton",
		"selectedTopComponentId",
		"selectedComponentId",
		"hideNewTabLink",
		"hideHeader",
		"hideDescription",
		"hideSpecNumber",
		"showDetailedValues",
		"hidePriceAdjustment",
		"hideMarkup"
	],
	data() {
		return {
			
		}
	},
	watch: {
		bidProp() {
			if (this.bidProp && "id" in this.bidProp)
				this.fetchAllComponents(this.bidProp.id)
		},
		'$store.projectBidObjects'() {
			if (this.bidProp && "id" in this.bidProp)
				this.fetchAllComponents(this.bidProp.id)
		}
	},
	computed: {
		totalBidValue() {
			return this.bidComponentsNested.reduce((acc,topComp) => acc + topComp.totalPriceRaw,0)
		},
		totalGrossMargin() {
			const totalSelling = this.totalBidValue
			if (!totalSelling) return 0
			const totalCost = this.bidComponentsNested.reduce((acc, topComp) => acc + (topComp.totalComponentCostRaw || 0), 0)
			const margin = ((totalSelling - totalCost) / totalSelling) * 100
			return isNaN(margin) ? 0 : margin
		},
		headerColSpan() {
			let cols = 5

			if (this.hideDescription)
				cols--

			return cols
		},
		grandTotalLabelColSpan() {
			let cols = 7

			if (this.hideDescription)
				cols--

			if (this.hideSpecNumber)
				cols--

			if (!this.showDetailedValues)
				cols -= 2

			if (!this.hidePriceAdjustment)
				cols++

			if (!this.hideMarkup)
				cols++

			return cols
		}
	},
	methods: {

		
	},
	created() {
		if (this.bidProp && "id" in this.bidProp)
			this.fetchAllComponents(this.bidProp.id)
	}
}
</script>

<style scoped>
	.active-section {
		background-color: var(--brand-primary-color-faded)!important;
	}

	.top-level-component {
		font-weight: bold;
	}
</style>